import ajax from '../../tools/ajax';
import load from '../../tools/load';
import setIcon from '../define/setIcon';
import { getAppEnv } from '@ep/tools/env';
import { inferProductFromHost } from '@ep/config/domainMap';
import session from '@ep/tools/session';
import Loader from '@ep/tools/loader';
import { resolveKnownImageSource } from './image';
import msgbox from '../../tools/msgbox';

const APP_PAGE_MODULES = import.meta.webpackContext('@AppPages/', {
  recursive: true,
  regExp: /\.js$/,
});
const APP_PAGE_KEYS = new Set(APP_PAGE_MODULES.keys());
const normalizeAppPagePath = (page) => {
  const normalized = String(page || '')
    .trim()
    .replace(/^\/+|\/+$/g, '')
    .replace(/\\/g, '/');
  if (!normalized) return null;
  return normalized.endsWith('.js') ? normalized : `${normalized}.js`;
};
const loadOptionalAppPageModule = async (page) => {
  const normalized = normalizeAppPagePath(page);
  if (!normalized) return null;
  const key = `./${normalized}`;
  if (!APP_PAGE_KEYS.has(key)) return null;

  try {
    return await APP_PAGE_MODULES(key);
  } catch (error) {
    console.warn(`Falha ao carregar pagina "${normalized}":`, error?.message || error);
    return null;
  }
};

class Menu {
  constructor() {
    this.dics = [];
    this.routeOptions = {};
    this.routeMap = new Map();
  }

  #resolveDicValue(dicName, itemKey, field = null) {
    const entry = window?.dic?.[dicName]?.[itemKey];
    if (!entry) return '';

    if (field) {
      const value = entry?.[field];
      if (typeof value === 'string') return value;
    }

    if (typeof entry === 'string') return entry;
    if (typeof entry?.menu === 'string') return entry.menu;
    if (typeof entry?.catalog === 'string') return entry.catalog;
    if (typeof entry?.display === 'string') return entry.display;

    return '';
  }

  #parseDicKey(rawKey) {
    const value = String(rawKey || '').trim();
    if (!value.startsWith('dic.')) return null;
    const parts = value.split('.');
    if (parts.length < 3) return null;
    return {
      dicName: parts[1],
      itemKey: parts.slice(2).join('.')
    };
  }

  #parseDicSource(rawSource) {
    const [dicName, field] = String(rawSource || '').split('|');
    const normalizedDicName = String(dicName || '').trim();
    if (!normalizedDicName) return null;

    return {
      dicName: normalizedDicName,
      field: String(field || '').trim() || null
    };
  }

  #resolveDicsValue(itemKey) {
    for (const source of this.dics) {
      const parsed = this.#parseDicSource(source);
      if (!parsed) continue;

      const value = this.#resolveDicValue(parsed.dicName, itemKey, parsed.field);
      if (value) return value;
    }

    return '';
  }

  #compactKey(rawKey) {
    const value = String(rawKey || '').trim();
    if (!value) return '';
    const parts = value.split('.');
    return String(parts[parts.length - 1] || '').trim() || value;
  }

  #freezeHeight(submenu, height) {
    submenu.style.transition = 'none';
    submenu.style.maxHeight = `${height}px`;
  }

  #restoreTransition(submenu) {
    submenu.style.removeProperty('transition');
  }

  #normalizeSlug(rawSlug) {
    if (typeof rawSlug !== 'string') return null;

    const cleaned = rawSlug
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase()
      .replace(/^\/+|\/+$/g, '');

    if (!cleaned || cleaned === 'null' || cleaned === 'undefined') {
      return null;
    }

    return cleaned;
  }

  #buildLeafSlug(item) {
    const explicitSlug = String(item?.slug || '').trim();
    if (explicitSlug) return this.#normalizeSlug(explicitSlug);

    const product = String(item?.product || '').trim();
    const category = String(item?.category || '').trim();
    const domain = String(item?.domain || '').trim();
    const module = String(item?.module || '').trim();
    const key = String(item?.key || '').trim();
    const method = String(item?.method || item?.template || '').trim();

    // Padrão canônico: product/category/domain/module[/key].
    if (product && category && domain && module) {
      const baseSlug = `${product}/${category}/${domain}/${module}`;
      return this.#normalizeSlug(key ? `${baseSlug}/${this.#compactKey(key)}` : baseSlug);
    }

    if (method) return this.#normalizeSlug(method);

    const rawKey = String(item?.key || '').trim();
    if (rawKey) return this.#normalizeSlug(this.#compactKey(rawKey));
    return null;
  }

  #registerLeafRoute(entry) {
    if (!entry?.slug) return;

    if (!this.routeMap.has(entry.slug)) {
      this.routeMap.set(entry.slug, entry);
    } else {
      console.warn(`Slug de menu duplicado para "${entry.slug}". Usando o primeiro item registrado.`);
    }
    const item = entry.item || {};
    if (item.template && item.product && item.category && item.domain && item.module) {
      const moduleSlug = this.#normalizeSlug(`${item.product}/${item.category}/${item.domain}/${item.module}`);
      if (!this.routeMap.has(moduleSlug)) this.routeMap.set(moduleSlug, entry);
    }
  }

  /** Module indisponível no trial: nenhuma navegação nem execução do Module. "Sim" abre o Module de contratação existente. */
  #showTrialRestriction(item) {
    const copy = item.trialRestriction === 'trial_consumed'
      ? dic.trial?.consumed
      : item.trialRestriction === 'byok_required'
        ? dic.trial?.byokRequired
        : dic.trial?.moduleUnavailable;
    if (!copy) return;
    const target = item.trialContracting;
    const entry = target
      ? this.routeMap.get(this.#normalizeSlug(`${item.product}/${target.category}/${target.domain}/${target.module}`))
      : null;
    if (!entry) {
      msgbox({ type: 'info', title: copy.title, message: copy.informative || copy.message });
      return;
    }
    msgbox({
      type: 'yn',
      title: copy.title,
      message: copy.message,
      click: async () => {
        try {
          this.#setActiveLeaf(entry);
          await this.#executeLeaf(entry, 'trial-contracting');
        } catch (error) {
          console.error('Erro ao abrir a contratação:', error);
        }
      },
    });
  }

  #prepareClose(submenu) {
    submenu.style.maxHeight = `${submenu.scrollHeight}px`;
  }

  #prepareOpen(li, submenu, targetHeight = null) {
    li.classList.add('open');
    submenu.style.maxHeight = `${targetHeight ?? submenu.scrollHeight}px`;

    const onTransitionEnd = (event) => {
      if (event.propertyName !== 'max-height') return;
      if (li.classList.contains('open')) {
        submenu.style.maxHeight = '9999px';
      }
      submenu.removeEventListener('transitionend', onTransitionEnd);
    };

    submenu.addEventListener('transitionend', onTransitionEnd);
  }

  #toggleSubmenu(li, subMenu) {
    const shouldOpen = !li.classList.contains('open');
    const siblings = li.parentElement ? Array.from(li.parentElement.children) : [];
    const toClose = [];

    siblings.forEach((sibling) => {
      if (sibling !== li && sibling.classList.contains('open')) {
        const siblingSubmenu = sibling.querySelector(':scope > ul.ep-submenu');
        if (siblingSubmenu) {
          this.#prepareClose(siblingSubmenu);
          toClose.push({ li: sibling, submenu: siblingSubmenu });
        }
      }
    });

    if (!shouldOpen) {
      const currentHeight = subMenu.scrollHeight;
      this.#freezeHeight(subMenu, currentHeight);
      li.offsetHeight;
      requestAnimationFrame(() => {
        this.#restoreTransition(subMenu);
        subMenu.style.maxHeight = '0px';
        li.classList.remove('open');
      });
      return;
    }

    const openHeight = subMenu.scrollHeight;
    this.#freezeHeight(subMenu, 0);
    toClose.forEach(({ submenu }) => {
      this.#freezeHeight(submenu, submenu.scrollHeight);
    });

    li.offsetHeight;

    requestAnimationFrame(() => {
      toClose.forEach(({ li: sibling, submenu }) => {
        this.#restoreTransition(submenu);
        submenu.style.maxHeight = '0px';
        sibling.classList.remove('open');
      });

      this.#restoreTransition(subMenu);
      this.#prepareOpen(li, subMenu, openHeight);
    });
  }

  #openAncestors(ancestorLis = []) {
    ancestorLis.forEach((ancestorLi) => {
      const subMenu = ancestorLi.querySelector(':scope > ul.ep-submenu');
      if (!subMenu) return;
      ancestorLi.classList.add('open');
      subMenu.style.maxHeight = '9999px';
    });
  }

  #setActiveLeaf(entry = null) {
    const li = entry?.li || null;
    if (!li) return;

    const scope = li.closest('.ep-menu-aside') || document;
    scope.querySelectorAll('.menu-action.menu-active').forEach((elem) => {
      elem.classList.remove('menu-active');
    });

    const current = li.querySelector(':scope > div.menu-action');
    if (current) current.classList.add('menu-active');
  }

  async #executeLeaf(entry, source = 'click', triggerEvent = null, navigation = {}) {
    const item = entry?.item || {};
    const moduleSlug = String(item?.module || item?.key || entry?.slug || '').trim();
    if (moduleSlug) {
      session.set('slug', moduleSlug);
    }
    const payload = {
      slug: entry?.slug || null,
      item,
      label: entry?.label || '',
      source
    };

    if (typeof this.routeOptions?.onNavigate === 'function') {
      await this.routeOptions.onNavigate(payload);
    }

    let response = null;
    if (typeof item.action === 'function') {
      response = await item.action(triggerEvent);
    } else if (item.product && item.category && item.domain && item.module && item.template) {
      response = await load.component({
        product: item.product,
        service: item.service || item.category,
        category: item.category,
        domain: item.domain,
        module: item.module,
        template: item.template,
        menuKey: item.key || null,
        configKey: item.configKey || item.screenKey || 'main',
        initialAction: navigation.initialAction,
        clear: item.clear || true
      });
    } else if (item.product && item.category && item.domain && item.module && item.key) {
      response = await ajax.post({
        product: item.product,
        service: item.service || item.category,
        category: item.category,
        domain: item.domain,
        module: item.module,
        method: item.method || 'getConfigScreen',
        body: { key: item.key },
      });
    }

    if (typeof this.routeOptions?.onAfterNavigate === 'function') {
      await this.routeOptions.onAfterNavigate({ ...payload, response });
    }

    return response;
  }

  async openBySlug(rawSlug, options = {}) {
    const slug = this.#normalizeSlug(rawSlug);
    if (!slug) return false;

    const entry = this.routeMap.get(slug);
    if (!entry) return false;

    this.#openAncestors(entry.ancestorLis);
    this.#setActiveLeaf(entry);
    await this.#executeLeaf(entry, options.source || 'route', null, options);
    return true;
  }

  syncIcons(items = []) {
    const visit = (entries) => {
      for (const item of Array.isArray(entries) ? entries : []) {
        const entry = this.routeMap.get(this.#buildLeafSlug(item));
        const row = entry?.li?.querySelector(':scope > div');
        if (row?.isConnected) {
          const current = row.querySelector(':scope > i');
          const previousIcon = entry.item?.icon;
          const nextIcon = item?.icon;
          const unchanged = current && previousIcon?.type === nextIcon?.type
            && previousIcon?.name === nextIcon?.name;
          if (!unchanged) {
            current?.remove();
            const icon = nextIcon?.name
              ? setIcon(row, { icon: { ...nextIcon, color: undefined } })
              : null;
            row.classList.toggle('menu-without-icon', !icon);
            entry.item = { ...entry.item, icon: nextIcon || null };
          }
        }
        visit(item?.items);
      }
    };
    visit(items);
  }

  #generateContent(elem, items, level = 0, ancestorLis = []) {
    items.forEach((item) => {
      const li = document.createElement('li');
      elem.appendChild(li);

      let text = '';
      const parsedDicKey = this.#parseDicKey(item?.key);

      if (item.label) {
        text = item.label;
      } else if (parsedDicKey && window?.dic?.[parsedDicKey.dicName]) {
        text = this.#resolveDicValue(parsedDicKey.dicName, parsedDicKey.itemKey)
          || parsedDicKey.itemKey;
      } else if (item.key) {
        const compactKey = this.#compactKey(item.key);
        text = this.#resolveDicsValue(compactKey) || compactKey;
      }

      const div = document.createElement('div');
      const children = Array.isArray(item.items) ? item.items : [];
      const hasChildren = children.length > 0;
      const isLeafWithAction = !hasChildren && (
        typeof item.action === 'function'
        || (item.product && item.category && item.domain && item.module && item.key)
        || (item.server && item.module && item.key)
        || !!item.template
      );

      if (getAppEnv() !== 'prod' && item.status) {
        div.classList.add(`menu-status-${String(item.status).trim().toLowerCase()}`);
      }

      const isEcosystemGroup = item.key === 'core' && hasChildren && level === 0;
      const itemIcon = isEcosystemGroup && !item.icon?.name
        ? { type: 'solid', name: 'earth-americas' }
        : item.icon;
      if (itemIcon) {
        // Na navegação, cores salvas no catálogo podem coincidir com o fundo.
        // Os ícones seguem a cor do menu em todos os estados.
        const icon = setIcon(li, { icon: { ...itemIcon, color: undefined } });
        div.appendChild(icon);
      } else {
        div.classList.add('menu-without-icon');
      }

      const span = document.createElement('span');
      span.textContent = text;
      div.appendChild(span);

      if (hasChildren) {
        div.classList.add('menu-toggle');
      }

      const isDisabled = item?.disabled === true;
      if (isDisabled) {
        const disabledReasonKey = String(item?.disabledReason || '').trim();
        const parsedDisabledReason = this.#parseDicKey(disabledReasonKey);
        const disabledReason = parsedDisabledReason
          ? this.#resolveDicValue(parsedDisabledReason.dicName, parsedDisabledReason.itemKey)
          : disabledReasonKey;

        div.classList.add('menu-disabled');
        if (item?.trialRestriction) div.classList.add('menu-trial-restricted');
        div.setAttribute('aria-disabled', 'true');
        if (disabledReason) {
          div.setAttribute('title', disabledReason);
          div.setAttribute('aria-label', `${text}. ${disabledReason}`);
        }
      }

      li.appendChild(div);

      if (isLeafWithAction) {
        div.classList.add('menu-action');

        const slug = this.#buildLeafSlug(item);
        const entry = {
          slug,
          item,
          label: text,
          li,
          ancestorLis: [...ancestorLis]
        };

        if (slug) {
          this.#registerLeafRoute(entry);
        }

        li.addEventListener('click', async (event) => {
          if (isDisabled) {
            event.preventDefault();
            event.stopPropagation();
            // Trial restrito: o item segue visível e atenuado; o clique só explica a restrição e oferece a contratação.
            if (item?.trialRestriction) this.#showTrialRestriction(item);
            return;
          }

          try {
            this.#setActiveLeaf(entry);
            await this.#executeLeaf(entry, 'click', event);
          } catch (error) {
            console.error('Erro ao executar item de menu:', error);
          }
        });
      }

      if (hasChildren) {
        const subMenu = document.createElement('ul');
        subMenu.classList.add('ep-submenu');
        subMenu.style.maxHeight = '0px';
        li.appendChild(subMenu);
        div.addEventListener('click', (event) => {
          event.stopPropagation();
          this.#toggleSubmenu(li, subMenu);
        });

        const nextAncestors = [...ancestorLis, li];
        this.#generateContent(subMenu, children, level + 1, nextAncestors);
      }
    });
  }

  #buildTopBrandNode(branding = {}) {
    const logoUrl = resolveKnownImageSource(branding.logoUrl, 'product');
    return {
      element: 'a',
      href: '/',
      content: [
        {
          element: 'image',
          origin: 'external',
          src: logoUrl,
          alt: branding.logoAlt || 'Logo',
          hasFigure: false,
          class: branding.logoClass || 'header-logo'
        },
        {
          element: 'div',
          class: branding.lockupClass || 'brand-lockup',
          content: [
            {
              element: 'div',
              class: branding.titleClass || 'brand-title',
              content: [
                { element: 'span', text: branding.title1 || '' },
                { element: 'span', class: branding.titleAccentClass || 'yellow-p', text: branding.title2 || '' },
              ]
            },
            { element: 'div', class: branding.subtitleClass || 'brand-subtitle', text: branding.subtitle || '' }
          ]
        }
      ]
    };
  }

  #createTopController(menuSelector = '.menubar', menuRoot = document) {
    const blockClickOptions = { capture: true };
    let activeMenuKey = null;
    let allowClickOnActive = false;
    let menuIndicator = null;
    let menuBarElement = null;
    let menuConnectionObserver = null;
    let menuResizeObserver = null;

    const blockClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      return false;
    };

    const ensureMenuIndicator = () => {
      const root = menuRoot && typeof menuRoot.querySelector === 'function' ? menuRoot : document;
      menuBarElement = root.querySelector(menuSelector);
      if (!menuBarElement) return;
      const existing = menuBarElement.querySelector('.menu-indicator');
      if (existing) {
        menuIndicator = existing;
        return;
      }

      const indicator = document.createElement('span');
      indicator.className = 'menu-indicator';
      menuBarElement.appendChild(indicator);
      menuIndicator = indicator;
    };

    const moveMenuIndicator = (target, attempt = 0) => {
      if (!menuIndicator || !menuBarElement || !target) return;
      if (!menuBarElement.isConnected) {
        if (!menuConnectionObserver && document.documentElement) {
          menuConnectionObserver = new MutationObserver(() => {
            if (!menuBarElement?.isConnected) return;
            menuConnectionObserver.disconnect();
            menuConnectionObserver = null;
            updateMenuIndicatorToActive();
          });
          menuConnectionObserver.observe(document.documentElement, { childList: true, subtree: true });
        }
        return;
      }
      const itemRect = target.getBoundingClientRect();
      const menuRect = menuBarElement.getBoundingClientRect();
      if (itemRect.width <= 0 && attempt < 12) {
        requestAnimationFrame(() => moveMenuIndicator(target, attempt + 1));
        return;
      }
      const isVertical = menuBarElement.classList.contains('is-mobile-vertical');
      const indicatorHeight = menuIndicator.getBoundingClientRect().height || 4;
      const left = isVertical ? 0 : itemRect.left - menuRect.left;
      const top = isVertical
        ? itemRect.bottom - menuRect.top - indicatorHeight
        : menuRect.height - indicatorHeight;
      menuIndicator.style.opacity = '1';
      menuIndicator.style.width = `${isVertical ? menuBarElement.clientWidth : itemRect.width}px`;
      menuIndicator.style.transform = `translate(${left}px, ${top}px)`;
    };

    const updateMenuIndicatorToActive = () => {
      if (!menuBarElement) return;
      const activeLink = menuBarElement.querySelector('a.active');
      if (activeLink) {
        moveMenuIndicator(activeLink);
        return;
      }
      if (menuIndicator) menuIndicator.style.opacity = '0';
    };

    const setActiveMenu = (key, options = {}) => {
      ensureMenuIndicator();
      allowClickOnActive = options?.allowClick === true;
      activeMenuKey = key;
      menuBarElement?.querySelectorAll(':scope > a').forEach((el) => {
        const isActive = key != null && el.dataset?.key === key;
        el.classList.toggle('active', isActive);
        el.classList.toggle('is-reclickable', isActive && allowClickOnActive);
        el.removeEventListener('click', blockClick, blockClickOptions);
        if (isActive && !allowClickOnActive) {
          el.addEventListener('click', blockClick, blockClickOptions);
        }
        if (isActive) el.setAttribute('aria-current', 'page');
        else el.removeAttribute('aria-current');
      });
      updateMenuIndicatorToActive();
    };

    const initMenuIndicator = () => {
      ensureMenuIndicator();
      if (!menuBarElement) return;

      menuBarElement.querySelectorAll('a').forEach((link) => {
        link.addEventListener('mouseenter', () => moveMenuIndicator(link));
        link.addEventListener('pointerenter', () => moveMenuIndicator(link));
        link.addEventListener('focus', () => moveMenuIndicator(link));
      });

      // O item ativo pode ser definido pelo layout depois que o menu é criado.
      // Nesse caso, reposiciona o indicador assim que a classe `active` chegar.
      const activeObserver = new MutationObserver(updateMenuIndicatorToActive);
      activeObserver.observe(menuBarElement, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
      });

      menuBarElement.addEventListener('mouseleave', updateMenuIndicatorToActive);
      menuBarElement.addEventListener('focusout', () => {
        requestAnimationFrame(() => {
          if (!menuBarElement?.contains(document.activeElement)) {
            updateMenuIndicatorToActive();
          }
        });
      });

      if (typeof ResizeObserver === 'function') {
        menuResizeObserver = new ResizeObserver(updateMenuIndicatorToActive);
        menuResizeObserver.observe(menuBarElement);
        menuBarElement.querySelectorAll('a').forEach((link) => menuResizeObserver.observe(link));
      }

      window.addEventListener('resize', updateMenuIndicatorToActive);
      updateMenuIndicatorToActive();
      requestAnimationFrame(updateMenuIndicatorToActive);
    };

    return {
      setActiveMenu,
      initMenuIndicator,
      getActiveMenuKey: () => activeMenuKey,
    };
  }

  #buildTopMenuItems(items = [], onItemClick = null) {
    let col = -1;
    return items
      .filter((item) => item?.visible !== false)
      .map((item) => {
        col += 1;
        return {
          element: 'a',
          href: item.href || '/',
          data: { key: item.key },
          col,
          row: 0,
          text: item.text || item.key,
          click: async (e) => {
            e.preventDefault();
            if (typeof onItemClick === 'function') {
              await onItemClick(item, e);
            }
          }
        };
      });
  }

  #resolveTopRouteEntry(routeMeta = {}, rawKey = '') {
    const token = String(rawKey || '').trim();
    if (!token) return null;

    const directEntry = routeMeta[token];
    if (directEntry) {
      return { routeKey: token, routeConfig: directEntry };
    }

    const normalizedToken = this.#normalizeTopSlug(token);
    if (!normalizedToken) return null;

    const matchedEntry = Object.entries(routeMeta).find(([, meta]) => {
      if (!meta || typeof meta !== 'object') return false;
      const slug = typeof meta.slug === 'string' ? this.#normalizeTopSlug(meta.slug) : null;
      if (slug && slug === normalizedToken) return true;
      return Array.isArray(meta.aliases)
        && meta.aliases.some((alias) => this.#normalizeTopSlug(alias) === normalizedToken);
    });

    if (!matchedEntry) return null;

    const [routeKey, routeConfig] = matchedEntry;
    return { routeKey, routeConfig };
  }

  #buildTopRouteEntries(routeMeta = {}, menuOrder = []) {
    const orderedKeys = Array.isArray(menuOrder) && menuOrder.length > 0
      ? menuOrder
      : Object.keys(routeMeta);

    return orderedKeys
      .map((token) => this.#resolveTopRouteEntry(routeMeta, token))
      .filter(Boolean);
  }

  #buildTopMenuItemsFromRouteMeta(routeMeta = {}, menuLabelByKey = {}, menuOrder = [], onItemClick = null) {
    const mapped = this.#buildTopRouteEntries(routeMeta, menuOrder)
      .map(({ routeKey, routeConfig }) => {
        if (!routeConfig || routeConfig.show === false || routeConfig.showInMenu === false) return null;
        const slug = typeof routeConfig.slug === 'string' ? routeConfig.slug.trim() : '';
        const href = slug ? `/${slug}` : '/';
        const label = menuLabelByKey?.[routeKey]
          || routeConfig.menu
          || routeConfig.catalog
          || routeConfig.display
          || routeKey;
        return {
          key: routeKey,
          href,
          text: label,
          visible: true,
        };
      })
      .filter(Boolean);

    return this.#buildTopMenuItems(mapped, onItemClick);
  }

  #buildTopRightContent(right = {}) {
    if (Array.isArray(right.content)) {
      return {
        element: 'div',
        class: right.class || 'buttonbar',
        isGrid: right.isGrid,
        content: right.content,
      };
    }

    if (Array.isArray(right.buttons)) {
      return {
        element: 'div',
        class: right.class || 'buttonbar',
        content: right.buttons,
      };
    }

    if (Array.isArray(right.links)) {
      return {
        element: 'div',
        class: right.class || 'social-contact',
        content: right.links,
      };
    }

    return null;
  }

  #normalizeTopSlug(rawSlug) {
    if (typeof rawSlug !== 'string') return null;
    const cleaned = rawSlug
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase()
      .replace(/^\/+|\/+$/g, '');
    if (!cleaned || cleaned === 'null' || cleaned === 'undefined') return null;
    return cleaned;
  }

  #resolveTopRouteBySlug(routeMeta = {}, rawSlug = '') {
    const slug = this.#normalizeTopSlug(rawSlug);
    if (!slug) return null;

    const [baseSlug, ...parts] = slug.split('/');
    const nestedSlug = parts.length > 0 ? parts.join('/') : null;

    const routeEntry = Object.entries(routeMeta).find(([, meta]) => {
      if (!meta) return false;
      if (meta.slug === baseSlug) return true;
      return Array.isArray(meta.aliases) && meta.aliases.includes(baseSlug);
    });

    if (!routeEntry) return null;
    return { routeKey: routeEntry[0], nestedSlug };
  }

  #normalizeTopRouteMeta(routeMetaInput = null) {
    if (Array.isArray(routeMetaInput)) {
      return routeMetaInput.reduce((acc, item) => {
        const key = String(item?.key || '').trim();
        if (!key) return acc;
        acc[key] = { ...(item && typeof item === 'object' ? item : {}) };
        return acc;
      }, {});
    }

    if (routeMetaInput && typeof routeMetaInput === 'object') {
      return { ...routeMetaInput };
    }

    return {};
  }

  #trackTopPageView() {
    const payload = {
      page_title: document.title,
      page_path: window.location.pathname || '/',
      page_location: window.location.href
    };

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', payload);
    }

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: 'page_view', ...payload });
    }
  }

  async #createTop(options = {}) {
    const {
      config = {},
      dic = {},
      activeMenu = 1,
      menuItems = [],
      routeMeta = null,
      menuLabelByKey = {},
      menuOrder = [],
      onMenuItemClick = null,
      parent = null,
      className = 'ep-menu-top',
      topRouting = null,
      showSlug = undefined,
      beforeRoute = null,
      afterRoute = null,
      onReady = null,
    } = options;
    const topConfig = config && typeof config === 'object' ? config : {};
    const topDic = dic && typeof dic === 'object' ? dic : {};
    const configRouteMeta = (Array.isArray(topConfig.routes) || (topConfig.routes && typeof topConfig.routes === 'object'))
      ? topConfig.routes
      : null;
    const configMenuOrder = Array.isArray(topConfig.order) ? topConfig.order : [];
    const configTopRoutingOptions = (
      topConfig.defaultRouteKey
      || topConfig.protectedRouteKey
      || topConfig.unknownRoutePath
    ) ? {
      defaultRouteKey: topConfig.defaultRouteKey || null,
      protectedRouteKey: topConfig.protectedRouteKey || null,
      unknownRoutePath: topConfig.unknownRoutePath || null,
    } : {};
    const topRoutingOptions = {
      ...configTopRoutingOptions,
      ...(topRouting && typeof topRouting === 'object' ? topRouting : {}),
    };
    const globalShowSlug = typeof topConfig.showSlug === 'boolean'
      ? topConfig.showSlug
      : (showSlug !== false);
    const derivedRouteMeta = this.#normalizeTopRouteMeta(configRouteMeta || routeMeta);
    const derivedMenuOrder = configMenuOrder.length > 0 ? configMenuOrder : menuOrder;
    const derivedMenuLabelByKey = topDic.menu && typeof topDic.menu === 'object' ? topDic.menu : menuLabelByKey;
    const derivedRouteEntries = this.#buildTopRouteEntries(derivedRouteMeta, derivedMenuOrder);
    const resolvedParent = parent ?? 'header';
    const configRouteCreator = async ({ routeKey, routeConfig, nestedSlug = null }) => {
      const targetProduct = String(routeConfig?.product || '').trim().toLowerCase();
      const currentProduct = inferProductFromHost(window.location?.hostname || '');
      if (targetProduct && targetProduct !== currentProduct && typeof window?.epGoToProduct === 'function') {
        const explicitPath = typeof routeConfig?.path === 'string' ? routeConfig.path.trim() : '';
        const slugPath = typeof routeConfig?.slug === 'string' && routeConfig.slug.trim()
          ? `/${routeConfig.slug.trim()}`
          : '/';
        const destinationPath = explicitPath || slugPath;
        await window.epGoToProduct(targetProduct, {
          path: destinationPath,
          remember: routeConfig?.remember !== false,
          ttlSec: Number(routeConfig?.ssoTtlSec || 60),
        });
        return true;
      }

      if (routeConfig?.component === 'login') {
        const loginCode = String(routeConfig?.code || topConfig?.code || 'platform').trim();
        const redirectPath = String(routeConfig?.redirectPath || '/').trim();
        await create({
          element: 'login',
          code: loginCode,
          branding: window?.dic?.branding || topDic || {},
          onRedirect: async ({ authenticated, token }) => {
            if (authenticated) {
              if (token) {
                session.set('token', token);
              }
              window.history.replaceState({ ...(window.history.state || {}), epAuthToken: token || null }, '', redirectPath);
              window.location.reload();
            }
          },
        });
        return true;
      }

      if (typeof window?.__SITE_NAVIGATE__ === 'function' && typeof routeConfig?.page === 'string' && routeConfig.page.trim()) {
        return window.__SITE_NAVIGATE__({ routeKey, routeConfig, nestedSlug });
      }

      if (typeof routeConfig?.page === 'string' && routeConfig.page.trim()) {
        const page = await loadOptionalAppPageModule(routeConfig.page.trim());
        if (typeof page?.default === 'function') {
          await page.default();
          return true;
        }

        if (page !== null) {
          return true;
        }

        const fallbackPage = await loadOptionalAppPageModule('unlogged');
        if (typeof fallbackPage?.default === 'function') {
          await fallbackPage.default();
        }
      }

      return true;
    };
    let controllerRef = null;
    const effectiveOnMenuItemClick = typeof onMenuItemClick === 'function'
      ? onMenuItemClick
      : async (item) => {
        if (!hasRouteMeta && item?.href) {
          if (controllerRef) {
            controllerRef.setActiveMenu(item.key);
          }

          if (typeof window?.__SITE_NAVIGATE__ === 'function') {
            await window.__SITE_NAVIGATE__({ routeConfig: { path: item.href } });
            return;
          }

          window.history.pushState({}, '', item.href);
          window.dispatchEvent(new PopStateEvent('popstate'));
          return;
        }

        if (!controllerRef) return;
        const routeHandler = typeof topRoutingOptions.createRoute === 'function'
          ? topRoutingOptions.createRoute
          : (typeof topRoutingOptions.onRoute === 'function' ? topRoutingOptions.onRoute : configRouteCreator);
        await controllerRef.navigateTop(item.key, {
          exposeSlug: globalShowSlug,
          createRoute: routeHandler,
          beforeRoute,
          afterRoute,
          analytics: topRoutingOptions.analytics === true,
        });
      };

    const hasRouteMeta = derivedRouteMeta
      && typeof derivedRouteMeta === 'object'
      && Object.keys(derivedRouteMeta).length > 0;
    const menuContent = hasRouteMeta
      ? this.#buildTopMenuItemsFromRouteMeta(derivedRouteMeta, derivedMenuLabelByKey, derivedMenuOrder, effectiveOnMenuItemClick)
      : this.#buildTopMenuItems(menuItems, effectiveOnMenuItemClick);
    await create([
      {
        element: 'div',
        parent: resolvedParent,
        class: className,
        isGrid: false,
        content: menuContent,
      }
    ]);
    const controllerRoot = resolvedParent && typeof resolvedParent.querySelector === 'function'
      ? resolvedParent
      : document;
    const controller = this.#createTopController(`.${className}`, controllerRoot);
    controllerRef = controller;
    const topRouteMeta = hasRouteMeta ? derivedRouteMeta : {};
    const topRouteKeys = derivedRouteEntries.map((entry) => entry.routeKey);
    const activeMenuRouteKey = Number.isInteger(activeMenu) && activeMenu > 0
      ? (topRouteKeys[activeMenu - 1] || null)
      : null;
    const getRouteConfigByKey = (routeKey) => topRouteMeta[routeKey] || null;
    const resolveRouteBySlug = (rawSlug) => this.#resolveTopRouteBySlug(topRouteMeta, rawSlug);
    const resolveRouteKeyBySlug = (rawSlug) => resolveRouteBySlug(rawSlug)?.routeKey || null;
    const normalizeSlug = (rawSlug) => this.#normalizeTopSlug(rawSlug);
    const getLanguagePrefix = () => {
      const languageConfig = window?.config?.language && typeof window.config.language === 'object'
        ? window.config.language
        : {};
      const defaultLang = typeof languageConfig.default === 'string' && languageConfig.default.trim()
        ? languageConfig.default.trim()
        : 'pt-BR';
      const supportedLangs = Array.isArray(languageConfig.supported) && languageConfig.supported.length > 0
        ? languageConfig.supported
        : [defaultLang];
      const activeLang = String(session.get('language') || document.documentElement.lang || defaultLang).trim();

      if (!supportedLangs.includes(activeLang) || activeLang === defaultLang) {
        return '';
      }

      return `/${activeLang}`;
    };
    const updateBrowserPath = (routeKey, { replace = false, nestedSlug = null } = {}) => {
      const config = getRouteConfigByKey(routeKey);
      if (!config) return;

      const explicitPath = typeof config.path === 'string' ? config.path.trim() : '';
      const slug = typeof config.slug === 'string' ? config.slug : '';
      const langPrefix = getLanguagePrefix();
      const basePath = explicitPath || (slug ? `/${slug}` : '/');
      const path = nestedSlug ? `${basePath.replace(/\/+$/g, '')}/${nestedSlug}` : basePath;
      const localizedPath = path === '/'
        ? (langPrefix || '/')
        : `${langPrefix}${path}`;

      if (window.location.pathname === localizedPath) return;
      const method = replace ? 'replaceState' : 'pushState';
      window.history[method]({}, '', localizedPath);
    };
    const hidePublicRouteInAddressBar = () => {
      const languageConfig = window?.config?.language && typeof window.config.language === 'object'
        ? window.config.language
        : {};
      const defaultLang = typeof languageConfig.default === 'string' && languageConfig.default.trim()
        ? languageConfig.default.trim()
        : 'pt-BR';
      const supportedLangs = Array.isArray(languageConfig.supported) && languageConfig.supported.length > 0
        ? languageConfig.supported
        : [defaultLang];
      const [firstSegment = ''] = String(window.location.pathname || '/').split('/').filter(Boolean);
      const explicitLangPrefix = supportedLangs.includes(firstSegment) ? `/${firstSegment}` : '';
      const homePath = explicitLangPrefix || '/';
      if (window.location.pathname === homePath) return;
      window.history.replaceState({}, '', homePath);
    };
    const clearMainContent = () => {
      const main = document.querySelector('main');
      if (!main) return;
      main.empty?.();
      main.innerHTML = '';
      main.classList.remove('module-main');
    };

    controller.route = {
      normalizeSlug,
      getRouteConfigByKey,
      resolveRouteBySlug,
      resolveRouteKeyBySlug,
      updateBrowserPath,
      hidePublicRouteInAddressBar,
    };

    controller.navigateTop = async (routeKey, navigateOptions = {}) => {
      const {
        exposeSlug = false,
        replaceSlug = false,
        preserveCurrentPathWhenHidden = false,
        nestedSlug = null,
        showLoader = true,
        createRoute = null,
        onRoute = null,
        onMissingRoute = null,
        beforeRoute = null,
        afterRoute = null,
        analytics = false,
      } = navigateOptions;
      const shouldExposeSlug = globalShowSlug && exposeSlug === true;

      const routeConfig = getRouteConfigByKey(routeKey);
      if (!routeConfig) {
        if (typeof onMissingRoute === 'function') {
          await onMissingRoute({ routeKey, nestedSlug, controller });
        }
        return false;
      }

      const loader = showLoader ? new Loader() : null;
      loader?.start();
      if (typeof beforeRoute === 'function') {
        try {
          await beforeRoute({ routeKey, routeConfig, nestedSlug, controller });
        } catch (error) {
          loader?.end();
          throw error;
        }
      }

      const routeHandler = typeof createRoute === 'function'
        ? createRoute
        : (typeof onRoute === 'function' ? onRoute : configRouteCreator);
      try {
        clearMainContent();
        if (typeof routeHandler === 'function') {
          const handled = await routeHandler({ routeKey, routeConfig, nestedSlug, controller });
          if (handled === false) return false;
        }

        controller.setActiveMenu(routeKey);
        if (shouldExposeSlug) {
          updateBrowserPath(routeKey, { replace: replaceSlug, nestedSlug });
        } else if (!preserveCurrentPathWhenHidden) {
          hidePublicRouteInAddressBar();
        }

        if (analytics) {
          this.#trackTopPageView();
        }

        if (typeof afterRoute === 'function') {
          await afterRoute({ routeKey, routeConfig, nestedSlug, controller });
        }

        return true;
      } finally {
        loader?.end();
      }
    };

    controller.initTopRoute = async (routeOptions = {}) => {
      const {
        initialSlug = null,
        initialSlugFromSessionKey = null,
        defaultRouteKey = null,
        activeMenuRouteKey = null,
        createRoute = null,
        onRoute = null,
        beforeRoute = null,
        afterRoute = null,
        onUnknownRoute = null,
        onProtectedRoute = null,
        isProtectedSlug = null,
        protectedRouteKey = null,
        unknownRoutePath = null,
        analytics = false,
      } = routeOptions;

      const routeHandler = typeof createRoute === 'function'
        ? createRoute
        : (typeof onRoute === 'function' ? onRoute : configRouteCreator);

      let sourceSlug = initialSlug;
      const currentPathname = typeof window !== 'undefined'
        ? String(window.location?.pathname || '').trim()
        : '';
      const isRootPath = currentPathname === '' || currentPathname === '/';
      if (!sourceSlug && initialSlugFromSessionKey) {
        sourceSlug = session.get(initialSlugFromSessionKey);
        session.delete(initialSlugFromSessionKey);
      }

      if (!sourceSlug && typeof window !== 'undefined') {
        sourceSlug = currentPathname.replace(/^\/+|\/+$/g, '');
      }

      if (isRootPath) {
        sourceSlug = '';
      }
      const normalized = normalizeSlug(sourceSlug);
      if (!normalized) {
        const fallbackRouteKey = activeMenuRouteKey || defaultRouteKey;
        return controller.navigateTop(fallbackRouteKey, {
          exposeSlug: false,
          showLoader: false,
          createRoute: routeHandler,
          beforeRoute,
          afterRoute,
          analytics,
        });
      }

      const resolved = resolveRouteBySlug(normalized);
      if (resolved?.routeKey) {
        return controller.navigateTop(resolved.routeKey, {
          exposeSlug: globalShowSlug,
          replaceSlug: true,
          preserveCurrentPathWhenHidden: true,
          nestedSlug: resolved.nestedSlug,
          showLoader: false,
          createRoute: routeHandler,
          beforeRoute,
          afterRoute,
          analytics,
        });
      }

      let shouldProtect = false;
      if (typeof isProtectedSlug === 'function') {
        shouldProtect = await isProtectedSlug(normalized);
      }

      if (shouldProtect) {
        if (protectedRouteKey) {
          return controller.navigateTop(protectedRouteKey, {
            exposeSlug: globalShowSlug,
            showLoader: false,
            createRoute: routeHandler,
            beforeRoute,
            afterRoute,
            analytics,
          });
        }

        if (typeof onProtectedRoute === 'function') {
          return onProtectedRoute({ slug: normalized, controller });
        }
        return false;
      }

      if (unknownRoutePath) {
        if (window.location.pathname !== unknownRoutePath) {
          window.history.replaceState({}, '', unknownRoutePath);
        }
        window.dispatchEvent(new PopStateEvent('popstate'));
        return true;
      }

      if (typeof onUnknownRoute === 'function') {
        return onUnknownRoute({ slug: normalized, controller });
      }
      return false;
    };

    if (Object.keys(topRoutingOptions).length > 0) {
      await controller.initTopRoute({
        ...topRoutingOptions,
        activeMenuRouteKey,
      });
    }

    controller.initMenuIndicator();

    if (Object.keys(topRoutingOptions).length === 0 && Number.isInteger(activeMenu) && activeMenu > 0) {
      const keys = Array.isArray(derivedMenuOrder) && derivedMenuOrder.length > 0
        ? derivedMenuOrder
        : (
          hasRouteMeta
            ? Object.keys(topRouteMeta)
            : menuItems.map((item) => item?.key).filter(Boolean)
        );
      const key = keys[activeMenu - 1];
      if (key) {
        controller.setActiveMenu(key);
      }
    }

    if (typeof onReady === 'function') {
      onReady(controller);
    }
    if (typeof window !== 'undefined') {
      window.__epTopMenuController = controller;
    }
    return controller;
  }

  async create(options) {
    if (options?.type === 'top') {
      return this.#createTop(options);
    }

    this.dics = options.dics || [];
    this.routeOptions = options.route || {};
    this.routeMap = new Map();

    const items = options.items || [];
    const elem = document.createElement('ul');
    if (options.type === 'aside') {
      elem.classList.add('ep-menu-aside');
    } else {
      elem.classList.add('ep-menu-nav');
    }

    this.#generateContent(elem, items);
    document.querySelector(options.parent).appendChild(elem);

    const initialSlug = this.#normalizeSlug(this.routeOptions?.slug || '');
    if (!initialSlug) return;

    const hasOpened = await this.openBySlug(initialSlug, { source: 'route' });
    if (!hasOpened && typeof this.routeOptions?.onRouteMiss === 'function') {
      this.routeOptions.onRouteMiss({ slug: initialSlug });
    }
  }
}

const menu = new Menu();

export const openMenuBySlug = async (slug, options = {}) => menu.openBySlug(slug, options);
export const syncMenuIcons = (items = []) => menu.syncIcons(items);

export default (options) => menu.create(options);
